<template>
  <section class="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
    <div class="flex items-center gap-4">
      <div
        class="h-14 w-14 shrink-0 rounded-full bg-[#0B2447] text-white flex items-center justify-center text-lg font-bold"
        aria-hidden="true">
        {{ initials }}
      </div>
      <div>
        <h2 class="text-lg font-bold text-[#0B2447]">Profil</h2>
        <p class="text-gray-500 text-sm">
          Vos informations personnelles et vos coordonnées.
        </p>
      </div>
    </div>

    <form class="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5" novalidate @submit.prevent="submit">
      <div v-for="field in fields" :key="field.key">
        <label :for="`profile-${field.key}`" class="block text-sm font-medium text-[#0B2447] mb-1.5">
          {{ field.label }}
          <span v-if="!field.required" class="text-gray-400 font-normal">
            (facultatif)
          </span>
        </label>
        <input :id="`profile-${field.key}`" v-model="form[field.key]" :type="field.type"
          :autocomplete="field.autocomplete" :aria-invalid="errors[field.key] ? 'true' : 'false'"
          :aria-describedby="errors[field.key] ? `profile-${field.key}-error` : null"
          class="w-full rounded-xl border px-4 py-2.5 text-sm text-[#0B2447] bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2447]/30 focus:border-[#0B2447]"
          :class="errors[field.key] ? 'border-red-300' : 'border-gray-200'" @input="clearError(field.key)" />
        <p v-if="errors[field.key]" :id="`profile-${field.key}-error`" class="mt-1.5 text-xs text-red-600">
          {{ errors[field.key] }}
        </p>
      </div>

      <div class="sm:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-2">
        <p v-if="status" role="status" class="text-sm"
          :class="status.type === 'success' ? 'text-emerald-700' : 'text-red-600'">
          {{ status.message }}
        </p>
        <span v-else class="text-sm text-gray-400">
          {{ isDirty ? 'Modifications non enregistrées.' : 'Aucune modification.' }}
        </span>

        <div class="flex gap-3">
          <button type="button"
            class="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B2447]/30 disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="!isDirty || saving" @click="reset">
            Annuler
          </button>
          <button type="submit"
            class="rounded-xl bg-[#0B2447] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#123566] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0B2447] disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="!isDirty || saving">
            {{ saving ? 'Enregistrement…' : 'Enregistrer les modifications' }}
          </button>
        </div>
      </div>
    </form>
  </section>
</template>

<script>
const EMPTY_USER = { prenom: '', nom: '', email: '', telephone: '' }
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'ProfileSettings',
  props: {
    // Données de l'utilisateur connecté (à fournir par le parent)
    user: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['updated'],
  data() {
    const base = { ...EMPTY_USER, ...this.user }
    return {
      fields: [
        { key: 'prenom', label: 'Prénom', type: 'text', autocomplete: 'given-name', required: true },
        { key: 'nom', label: 'Nom', type: 'text', autocomplete: 'family-name', required: true },
        { key: 'email', label: 'Adresse e-mail', type: 'email', autocomplete: 'email', required: true },
        { key: 'telephone', label: 'Téléphone', type: 'tel', autocomplete: 'tel', required: false }
      ],
      form: { ...base },
      initial: { ...base },
      errors: {},
      saving: false,
      status: null
    }
  },
  computed: {
    initials() {
      const p = (this.form.prenom || '').trim().charAt(0)
      const n = (this.form.nom || '').trim().charAt(0)
      return (p + n).toUpperCase() || '?'
    },
    isDirty() {
      return Object.keys(EMPTY_USER).some((k) => this.form[k] !== this.initial[k])
    }
  },
  watch: {
    // Si le parent recharge l'utilisateur, on resynchronise le formulaire
    user: {
      deep: true,
      handler(value) {
        const base = { ...EMPTY_USER, ...value }
        this.form = { ...base }
        this.initial = { ...base }
      }
    },
    form: {
      deep: true,
      handler() {
        this.status = null
      }
    }
  },
  methods: {
    validate() {
      const e = {}
      if (!this.form.prenom.trim()) e.prenom = 'Indiquez votre prénom.'
      if (!this.form.nom.trim()) e.nom = 'Indiquez votre nom.'
      if (!this.form.email.trim()) {
        e.email = 'Indiquez votre adresse e-mail.'
      } else if (!EMAIL_RE.test(this.form.email.trim())) {
        e.email = 'Cette adresse e-mail n\'est pas valide. Exemple : nom@exemple.com'
      }
      this.errors = e
      return Object.keys(e).length === 0
    },
    clearError(key) {
      if (this.errors[key]) {
        this.errors = { ...this.errors, [key]: null }
      }
    },
    reset() {
      this.form = { ...this.initial }
      this.errors = {}
      this.status = null
    },
    async submit() {
      if (!this.validate()) return
      this.saving = true
      try {
        const payload = {
          prenom: this.form.prenom.trim(),
          nom: this.form.nom.trim(),
          email: this.form.email.trim(),
          telephone: this.form.telephone.trim()
        }
        await this.saveProfile(payload)
        this.form = { ...payload }
        this.initial = { ...payload }
        this.status = { type: 'success', message: 'Profil enregistré.' }
        this.$emit('updated', payload)
      } catch (err) {
        this.status = {
          type: 'error',
          message: 'L\'enregistrement a échoué. Vérifiez votre connexion et réessayez.'
        }
      } finally {
        this.saving = false
      }
    },
    // TODO : remplacer par ton vrai appel API, par exemple
    // return api.put('/me', payload)
    saveProfile(payload) {
      return new Promise((resolve) => setTimeout(() => resolve(payload), 600))
    }
  }
}
</script>
